/**
 * Default map viewport for the Charlotte metro coverage area.
 * Counties: Mecklenburg NC, Gaston NC, Union NC, York SC, Lancaster SC.
 * Coordinates are the approximate geographic center of the cluster (Charlotte CBD area).
 */
export const CHARLOTTE_METRO_COUNTY_SLUGS = [
  'mecklenburg-nc',
  'gaston-nc',
  'union-nc',
  'york-sc',
  'lancaster-sc',
];

export const CHARLOTTE_METRO_DEFAULT = {
  lat: 35.15,
  lng: -80.95,
  zoom: 10,
};

const MAP_VIEWPORT_STORAGE_KEY = 'ls_mapViewport';

/** @returns {{ lat: number, lng: number, zoom: number } | null} */
export function readStoredMapViewport() {
  try {
    const raw = localStorage.getItem(MAP_VIEWPORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    const zoom = Number(parsed?.zoom);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
    if (zoom < 1 || zoom > 21) return null;
    return { lat, lng, zoom };
  } catch {
    return null;
  }
}

export function writeStoredMapViewport({ lat, lng, zoom }) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return;
  localStorage.setItem(MAP_VIEWPORT_STORAGE_KEY, JSON.stringify({ lat, lng, zoom }));
}

/** Union bbox center from loaded region boundary paths, or null. */
export function centerFromRegionBoundaries(regionBoundaries, slugs) {
  if (!regionBoundaries || !slugs?.length) return null;
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  let found = false;

  for (const slug of slugs) {
    const paths = regionBoundaries[slug];
    if (!paths?.length) continue;
    found = true;
    paths.forEach((ring) => {
      ring.forEach((p) => {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      });
    });
  }

  if (!found || maxLat <= minLat || maxLng <= minLng) return null;
  return { lat: (maxLat + minLat) / 2, lng: (minLng + maxLng) / 2 };
}

/** Put Charlotte metro slugs first; preserves relative order within each group. */
export function sortSlugsCharlotteMetroFirst(slugs) {
  if (!Array.isArray(slugs) || slugs.length === 0) return slugs;
  const charlotte = new Set(CHARLOTTE_METRO_COUNTY_SLUGS);
  const primary = CHARLOTTE_METRO_COUNTY_SLUGS.filter((s) => slugs.includes(s));
  const rest = slugs.filter((s) => !charlotte.has(s));
  return [...primary, ...rest];
}
